import { ANALYTICS_ENDPOINT } from '../../utils/eventLogAnalytics';
import { analyticsAuthHeaders } from './analyticsListingApi';

/** Rows returned by /timeseries (array or paginated envelope). */
export const normalizeTimeseriesRows = (body) => {
  if (Array.isArray(body)) return { rows: body, total: body.length };
  const rows = body?.data || body?.items || body?.results || [];
  const total = body?.total ?? body?.totalCount ?? body?.count ?? rows.length;
  return { rows: Array.isArray(rows) ? rows : [], total };
};

/**
 * Walk the full timeseries for the selected window and return rows for
 * `eventKey`. Accepts either a single event key or an array of keys (e.g. the
 * share events, which span link_copied + the legacy link_shared). Submissions
 * also pull upload_failed + gallery_ready so groupEventsByUpload can resolve
 * outcomes.
 */
export async function fetchAllOccurrencesForEvent(
  eventKey,
  dateParams,
  partnerNameFilter
) {
  const base = `${ANALYTICS_ENDPOINT}/timeseries?${dateParams}&partnerName=${encodeURIComponent(partnerNameFilter)}`;
  const matches = [];
  const seen = new Set();

  const eventSet = Array.isArray(eventKey)
    ? new Set(eventKey)
    : eventKey === 'floorplan_uploaded'
      ? new Set(['floorplan_uploaded', 'upload_failed', 'gallery_ready'])
      : new Set([eventKey]);

  const collect = (rows) => {
    (rows || []).forEach((r) => {
      if (!eventSet.has(r?.meta?.event)) return;
      const id = r._id || `${r.timestamp}-${r.meta?.user}`;
      if (seen.has(id)) return;
      seen.add(id);
      matches.push(r);
    });
  };

  // Carry the logged-in admin/partnerAdmin Bearer token. /timeseries stays
  // public (optional auth), but BE PR #185 only attaches the captured-artifact
  // link (detail.uploadedFileUrl) when req.user is an admin/partnerAdmin —
  // anonymous requests get the link stripped, so the failed-row drill-down link
  // never appears. Sending the token here is what surfaces it. Mirrors the auth
  // header the sibling /signups-breakdown and /listings-summary fetches send.
  const authHeaders = analyticsAuthHeaders();

  try {
    const res = await fetch(base, { headers: authHeaders });
    const body = await res.json();
    if (res.ok) {
      const { rows } = normalizeTimeseriesRows(body);
      collect(rows);
      if (matches.length > 0) return matches;
    }
  } catch {
    // fall through to paginated walk
  }

  const pageSize = 300;
  const maxPages = 40;
  for (let page = 1; page <= maxPages; page++) {
    const res = await fetch(
      `${base}&interval=day&page=${page}&pageSize=${pageSize}`,
      { headers: authHeaders }
    );
    const body = await res.json();
    if (!res.ok) break;
    const { rows, total } = normalizeTimeseriesRows(body);
    collect(rows);
    if (!rows.length || page * pageSize >= total) break;
  }

  return matches.sort(
    (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
  );
}
